import type { MapSources, MapOldSources } from '@vueuse/core'
import type { WatchCallback, WatchStopHandle, WatchOptions, WatchSource } from 'vue'


/**
 * Custom watch wrapper that pauses watchers when component is deactivated in keep-alive.
 * Prevents memory leaks and unwanted side effects (like tracking events) from cached components.
 */

// Overload for single source
export function useWatch<T, Immediate extends Readonly<boolean> = false>(
  source: WatchSource<T>,
  cb: WatchCallback<T, Immediate extends true ? T | undefined : T>,
  options?: WatchOptions<Immediate>
): WatchStopHandle

// Overload for multiple sources
export function useWatch<T extends Readonly<WatchSource<unknown>[]>, Immediate extends Readonly<boolean> = false>(
  sources: [...T],
  cb: WatchCallback<MapSources<T>, MapOldSources<T, Immediate>>,
  options?: WatchOptions<Immediate>
): WatchStopHandle

// Overload for reactive objects
export function useWatch<T extends object, Immediate extends Readonly<boolean> = false>(
  source: T,
  cb: WatchCallback<T, Immediate extends true ? T | undefined : T>,
  options?: WatchOptions<Immediate>
): WatchStopHandle

export function useWatch(sources: any, cb: any, options?: any): WatchStopHandle {
  const activated = ref(true)

  onActivated(() => {
    activated.value = true
  })
  onDeactivated(() => {
    activated.value = false
  })

  return watch(sources, (...args: any[]) => {
    if (activated.value) {
      cb(...args)
    }
  }, options)
}

export default useWatch
